import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Grid,
  GridItem,
  useColorModeValue,
  Heading,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Divider,
  Image,
  Stack,
} from "@chakra-ui/react";
import { useQuery } from 'react-query';
import { getAppointments } from '../services/appointmentService';
import { getClients } from '../services/clientService';
import { getConsultants } from '../services/consultantService';
import JalaliDateDisplay from '../components/JalaliDateDisplay';
import { FaCalendarAlt, FaClock, FaUsers, FaUserTie, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaMoneyBillWave } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

function Dashboard() {
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const { user: currentUser } = useAuth();
  
  // دریافت تاریخ امروز
  const today = new Date();
  const todayString = today.toISOString().split('T')[0]; // YYYY-MM-DD format

  // دریافت تمام ملاقات‌ها
  const {
    data: appointmentsData,
    isLoading: isLoadingAppointments,
    error: appointmentsError
  } = useQuery(
    ['appointments', 'all'],
    () => getAppointments({ limit: 1000 }),
    {
      staleTime: 5 * 60 * 1000, // 5 دقیقه
    }
  );

  // دریافت آمار مراجعان
  const {
    data: clientsData,
    isLoading: isLoadingClients
  } = useQuery(
    ['clients', 'stats'],
    () => getClients({ limit: 1000 }),
    {
      staleTime: 10 * 60 * 1000, // 10 دقیقه
    }
  );

  // دریافت آمار مشاوران
  const {
    data: consultantsData,
    isLoading: isLoadingConsultants
  } = useQuery(
    ['consultants', 'stats'],
    () => getConsultants({ limit: 1000 }),
    {
      staleTime: 10 * 60 * 1000, // 10 دقیقه
    }
  );

  // محاسبه آمار
  const calculateStats = () => {
    if (!appointmentsData) return {};

    const appointments = appointmentsData.data?.appointments || appointmentsData.appointments || appointmentsData.data || appointmentsData || [];
    const clients = clientsData?.data?.clients || clientsData?.clients || clientsData?.data || clientsData || [];
    const consultants = consultantsData?.data?.consultants || consultantsData?.consultants || consultantsData?.data || consultantsData || [];

    // ملاقات‌های امروز
    const todayAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date).toISOString().split('T')[0];
      return aptDate === todayString;
    });

    // ملاقات‌های امروز که هنوز ساعتشان فرا نرسیده
    const upcomingTodayAppointments = todayAppointments.filter(apt => {
      const aptTime = apt.time;
      const currentTime = new Date().toTimeString().slice(0, 5); // HH:MM format
      return aptTime > currentTime;
    });

    // آمار وضعیت‌های ملاقات
    const appointmentStatusStats = appointments.reduce((acc, apt) => {
      const status = apt.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // آمار وضعیت‌های پرداخت
    const paymentStatusStats = appointments.reduce((acc, apt) => {
      const paymentStatus = apt.paymentStatus || 'unknown';
      acc[paymentStatus] = (acc[paymentStatus] || 0) + 1;
      return acc;
    }, {});

    // آمار ملاقات‌های امروز بر اساس وضعیت
    const todayStatusStats = todayAppointments.reduce((acc, apt) => {
      const status = apt.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // آمار ملاقات‌های امروز بر اساس وضعیت پرداخت
    const todayPaymentStats = todayAppointments.reduce((acc, apt) => {
      const paymentStatus = apt.paymentStatus || 'unknown';
      acc[paymentStatus] = (acc[paymentStatus] || 0) + 1;
      return acc;
    }, {});

    return {
      totalAppointments: appointments.length,
      todayAppointments: todayAppointments.length,
      upcomingTodayAppointments: upcomingTodayAppointments.length,
      totalClients: clients.length,
      totalConsultants: consultants.length,
      appointmentStatusStats,
      paymentStatusStats,
      todayStatusStats,
      todayPaymentStats,
      upcomingTodayAppointments
    };
  };

  const stats = calculateStats();

  // تبدیل وضعیت به فارسی
  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'در انتظار تأیید';
      case 'scheduled': return 'تأیید شده';
      case 'completed': return 'تکمیل شده';
      case 'cancelled': return 'لغو شده';
      case 'no-show': return 'عدم حضور';
      default: return status;
    }
  };

  // تبدیل وضعیت پرداخت به فارسی
  const getPaymentStatusText = (status) => {
    switch (status) {
      case 'pending': return 'در انتظار پرداخت';
      case 'paid': return 'پرداخت شده';
      case 'partial': return 'پرداخت جزئی';
      case 'refunded': return 'بازگشت وجه';
      default: return status;
    }
  };

  // رنگ‌های وضعیت
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'yellow';
      case 'confirmed': return 'blue';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      case 'no-show': return 'orange';
      default: return 'gray';
    }
  };

  // رنگ‌های وضعیت پرداخت
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'yellow';
      case 'paid': return 'green';
      case 'partial': return 'blue';
      case 'refunded': return 'red';
      default: return 'gray';
    }
  };

  if (isLoadingAppointments || isLoadingClients || isLoadingConsultants) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" color="teal.500" />
              <Text mr={4} fontSize="lg" color={textColor}>
                در حال بارگذاری داشبورد...
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    );
  }

  if (appointmentsError) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle>خطا در بارگذاری داشبورد!</AlertTitle>
              <AlertDescription>
                {appointmentsError.message || 'خطا در ارتباط با سرور'}
              </AlertDescription>
            </Alert>
          </Box>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction="column"  dir="rtl">
      <Box mx="auto"  pe="20px" minH="100vh">
        <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
          {/* باکس خوش‌آمدگویی */}
          <Box 
            bg="linear-gradient(135deg, teal.50 0%, teal.100 100%)"
            borderRadius="20px"
            p={8}
            mb={2}
            border="1px solid"
            borderColor="teal.200"
            position="relative"
            overflow="hidden"
          >
            {/* پس‌زمینه تزئینی */}
            <Box
              position="absolute"
              top="-50%"
              right="-20%"
              width="200px"
              height="200px"
              borderRadius="50%"
              bg="rgba(0, 128, 128, 0.1)"
              zIndex={1}
              pointerEvents="none"
            />
            <Box
              position="absolute"
              bottom="-30%"
              left="-10%"
              width="150px"
              height="150px"
              borderRadius="50%"
              bg="rgba(0, 128, 128, 0.08)"
              zIndex={1}
              pointerEvents="none"
            />
            
            <Flex 
              direction={{ base: "column", md: "row" }} 
              align="center" 
              justify="space-between"
              position="relative"
              zIndex={10}
            >
              <Stack spacing={4} flex="1">
                <Heading 
                  size="lg" 
                  color="teal.800" 
                  textAlign={{ base: "center", md: "right" }}
                  fontWeight="bold"
                >
                  خوش آمدید {currentUser?.name ? `${currentUser.name} عزیز` : ''}  
                </Heading>
                <Text color="teal.600" fontSize="sm" opacity={0.8}>
                  {currentUser?.role === 'manager' ? 'مدیر کل' : 
                   currentUser?.role === 'admin' ? 'مدیر' : 
                   currentUser?.role === 'consultant' ? 'مشاور' : 'کاربر'} سیستم
                </Text>
                <Text 
                  color="teal.700" 
                  fontSize="lg"
                  textAlign={{ base: "center", md: "right" }}
                  opacity={0.9}
                >
                  مرکز مشاوره نارون سبز
                </Text>
                <Text 
                  color="teal.600" 
                  fontSize="md"
                  textAlign={{ base: "center", md: "right" }}
                  opacity={0.8}
                >
                  امروز {new Date().toLocaleDateString('fa-IR')} است
                </Text>
              </Stack>
              
              <Box 
                ml={{ base: 0, md: 8 }}
                mb={{ base: 4, md: 0 }}
              >
                <Image
                  src="/logo.png"
                  alt="لوگو نارون سبز"
                  boxSize={{ base: "80px", md: "120px" }}
                  objectFit="contain"
                  borderRadius="20px"
                  bg="rgba(0, 128, 128, 0.1)"
                  p={3}
                  boxShadow="0 10px 20px 0 rgba(0, 0, 0, 0.1)"
                />
              </Box>
            </Flex>
          </Box>

          <Box p="5px 0px 10px 0px">
            <Flex direction="column">
              <Text fontSize="lg" color={textColor} fontWeight="bold" me="auto">
                داشبورد مدیریتی
              </Text>
              <Text fontSize="md" fontWeight="400" me="auto" color="gray.400">
                خلاصه‌ای از وضعیت سیستم و ملاقات‌های امروز
              </Text>
            </Flex>
          </Box>

          {/* آمار کلی */}
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={6} mb={8}>
            <GridItem>
              <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p={6}>
                <Stat>
                  <StatLabel color={textColor}>
                    <HStack spacing={2}>
                      <FaCalendarAlt color="teal" />
                      <Text>کل ملاقات‌ها</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color={textColor}>{stats.totalAppointments}</StatNumber>
                  <StatHelpText>تعداد کل ملاقات‌های ثبت شده</StatHelpText>
                </Stat>
              </Box>
            </GridItem>

            <GridItem>
              <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p={6}>
                <Stat>
                  <StatLabel color={textColor}>
                    <HStack spacing={2}>
                      <FaClock color="blue" />
                      <Text>ملاقات‌های امروز</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color={textColor}>{stats.todayAppointments}</StatNumber>
                  <StatHelpText>ملاقات‌های برنامه‌ریزی شده امروز</StatHelpText>
                </Stat>
              </Box>
            </GridItem>

            <GridItem>
              <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p={6}>
                <Stat>
                  <StatLabel color={textColor}>
                    <HStack spacing={2}>
                      <FaUsers color="green" />
                      <Text>کل مراجعان</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color={textColor}>{stats.totalClients}</StatNumber>
                  <StatHelpText>تعداد کل مراجعان ثبت شده</StatHelpText>
                </Stat>
              </Box>
            </GridItem>

            <GridItem>
              <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p={6}>
                <Stat>
                  <StatLabel color={textColor}>
                    <HStack spacing={2}>
                      <FaUserTie color="purple" />
                      <Text>کل مشاوران</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color={textColor}>{stats.totalConsultants}</StatNumber>
                  <StatHelpText>تعداد کل مشاوران فعال</StatHelpText>
                </Stat>
              </Box>
            </GridItem>
          </Grid>

          {/* ملاقات‌های امروز که هنوز ساعتشان فرا نرسیده */}
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" mb={8}>
            <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
              <Heading size="md" color={textColor}>
                <HStack spacing={2}>
                  <FaClock color="blue" />
                  <Text>ملاقات‌های امروز (قبل از ساعت فعلی)</Text>
                </HStack>
              </Heading>
            </Box>
            <Box p={6}>
              {stats.upcomingTodayAppointments.length > 0 ? (
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th color={textColor}>مراجع</Th>
                      <Th color={textColor}>مشاور</Th>
                      <Th color={textColor}>ساعت</Th>
                      <Th color={textColor}>مدت</Th>
                      <Th color={textColor}>وضعیت</Th>
                      <Th color={textColor}>پرداخت</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {stats.upcomingTodayAppointments.map((appointment) => (
                      <Tr key={appointment._id}>
                        <Td color={textColor}>
                          {appointment.client?.firstName && appointment.client?.lastName
                            ? `${appointment.client.firstName} ${appointment.client.lastName}`
                            : appointment.client?.name || 'نامشخص'}
                        </Td>
                        <Td color={textColor}>
                          {appointment.consultant?.name || 'نامشخص'}
                        </Td>
                        <Td color={textColor}>{appointment.time}</Td>
                        <Td color={textColor}>{appointment.duration} دقیقه</Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(appointment.status)}>
                            {getStatusText(appointment.status)}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={getPaymentStatusColor(appointment.paymentStatus)}>
                            {getPaymentStatusText(appointment.paymentStatus)}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Text textAlign="center" color="gray.500" p={4}>
                  هیچ ملاقات‌ای برای امروز قبل از ساعت فعلی وجود ندارد
                </Text>
              )}
            </Box>
          </Box>

          {/* گزارش وضعیت‌های ملاقات */}
          <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6} mb={8}>
            <GridItem>
              <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
                <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
                  <Heading size="md" color={textColor}>
                    <HStack spacing={2}>
                      <FaCheckCircle color="green" />
                      <Text>وضعیت ملاقات‌ها (امروز)</Text>
                    </HStack>
                  </Heading>
                </Box>
                <Box p={6}>
                  <VStack spacing={3} align="stretch">
                    {Object.entries(stats.todayStatusStats).map(([status, count]) => (
                      <Box key={status}>
                        <Flex justify="space-between" align="center" mb={2}>
                          <Text color={textColor} fontSize="sm">
                            {getStatusText(status)}
                          </Text>
                          <Badge colorScheme={getStatusColor(status)}>
                            {count}
                          </Badge>
                        </Flex>
                        <Progress 
                          value={(count / stats.todayAppointments) * 100} 
                          colorScheme={getStatusColor(status)}
                          size="sm"
                          borderRadius="md"
                        />
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </Box>
            </GridItem>

            <GridItem>
              <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
                <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
                  <Heading size="md" color={textColor}>
                    <HStack spacing={2}>
                      <FaMoneyBillWave color="green" />
                      <Text>وضعیت پرداخت‌ها (امروز)</Text>
                    </HStack>
                  </Heading>
                </Box>
                <Box p={6}>
                  <VStack spacing={3} align="stretch">
                    {Object.entries(stats.todayPaymentStats).map(([status, count]) => (
                      <Box key={status}>
                        <Flex justify="space-between" align="center" mb={2}>
                          <Text color={textColor} fontSize="sm">
                            {getPaymentStatusText(status)}
                          </Text>
                          <Badge colorScheme={getPaymentStatusColor(status)}>
                            {count}
                          </Badge>
                        </Flex>
                        <Progress 
                          value={(count / stats.todayAppointments) * 100} 
                          colorScheme={getPaymentStatusColor(status)}
                          size="sm"
                          borderRadius="md"
                        />
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </Box>
            </GridItem>
          </Grid>

          {/* گزارش کلی وضعیت‌ها */}
          <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6}>
            <GridItem>
              <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
                <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
                  <Heading size="md" color={textColor}>
                    <HStack spacing={2}>
                      <FaCheckCircle color="blue" />
                      <Text>وضعیت کلی ملاقات‌ها</Text>
                    </HStack>
                  </Heading>
                </Box>
                <Box p={6}>
                  <VStack spacing={3} align="stretch">
                    {Object.entries(stats.appointmentStatusStats).map(([status, count]) => (
                      <Box key={status}>
                        <Flex justify="space-between" align="center" mb={2}>
                          <Text color={textColor} fontSize="sm">
                            {getStatusText(status)}
                          </Text>
                          <Badge colorScheme={getStatusColor(status)}>
                            {count}
                          </Badge>
                        </Flex>
                        <Progress 
                          value={(count / stats.totalAppointments) * 100} 
                          colorScheme={getStatusColor(status)}
                          size="sm"
                          borderRadius="md"
                        />
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </Box>
            </GridItem>

            <GridItem>
              <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
                <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
                  <Heading size="md" color={textColor}>
                    <HStack spacing={2}>
                      <FaMoneyBillWave color="blue" />
                      <Text>وضعیت کلی پرداخت‌ها</Text>
                    </HStack>
                  </Heading>
                </Box>
                <Box p={6}>
                  <VStack spacing={3} align="stretch">
                    {Object.entries(stats.paymentStatusStats).map(([status, count]) => (
                      <Box key={status}>
                        <Flex justify="space-between" align="center" mb={2}>
                          <Text color={textColor} fontSize="sm">
                            {getPaymentStatusText(status)}
                          </Text>
                          <Badge colorScheme={getPaymentStatusColor(status)}>
                            {count}
                          </Badge>
                        </Flex>
                        <Progress 
                          value={(count / stats.totalAppointments) * 100} 
                          colorScheme={getPaymentStatusColor(status)}
                          size="sm"
                          borderRadius="md"
                        />
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </Box>
            </GridItem>
          </Grid>
        </Box>
      </Box>
    </Flex>
  );
}

export default Dashboard; 